import React, { useMemo, useState } from 'react';
import { useUsersStore } from '../store/useUsersStore';
import { markNotificationRead, getNotificationDetails } from '../utils/api';
import { useNotifications } from '../contexts/NotificationContext';
import { useModal } from '../contexts/ModalContext';
import ProjectDetailsModal from './ProjectDetailsModal';
import { useFirebaseAuth } from '../contexts/FirebaseAuthContext';
import { useNavigate } from 'react-router-dom';

const formatTime = (ts) => {
  const diff = Date.now() - ts;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
};

const NotificationBell = () => {
  const { items, unreadCount, markRead, markAllRead, remove } = useNotifications();
  const { modalState, openModal, closeModal } = useModal();
  const [open, setOpen] = useState(false);
  const [pendingIds, setPendingIds] = useState(new Set());
  const [pendingViewIds, setPendingViewIds] = useState(new Set());
  const { user: fbUser } = useFirebaseAuth();
  const navigate = useNavigate();
  const selectedSubUser = useUsersStore((s) => s.getSelectedUser && s.getSelectedUser());

  const grouped = useMemo(() => items, [items]);

  const handleMarkRead = async (notification) => {
    const id = notification.id;
    if (!id) return;
    const subUserId = selectedSubUser?.document_id || selectedSubUser?.sub_user_id || selectedSubUser?.id;
    setPendingIds((prev) => new Set(prev).add(id));
    try {
      if (!subUserId) {
        markRead(id);
        return;
      }
      const res = await markNotificationRead(subUserId, id, true);
      if (res && res.status >= 200 && res.status < 300) {
        markRead(id);
      } else {
        console.warn('mark-read failed', res?.status, res?.data);
        markRead(id);
      }
    } catch (err) {
      console.warn('mark-read error', err);
      markRead(id);
    } finally {
      setPendingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const handleView = async (notification) => {
    const subUserId = selectedSubUser?.document_id || selectedSubUser?.sub_user_id || selectedSubUser?.id;
    if (!subUserId) {
      openModal('projectDetails', notification.projectData || null);
      return;
    }

    const id = notification.id;
    try {
      setPendingViewIds(prev => new Set(prev).add(id));
      const idToken = typeof fbUser?.getIdToken === 'function' ? await fbUser.getIdToken() : null;
      const res = await getNotificationDetails(subUserId, notification.id, idToken);
      const d = res?.data?.data || res?.data || {};
      const projectData = {
        id: d.project_id || d.projectId || d.id,
        title: d.title || d.project_title || '',
        description: d.description || d.body || '',
        amount: d.bid_amount || d.amount || 0,
        currency: d.currency || 'USD',
        currencySign: d.currency_sign || d.currencySign || '$',
        seo_url: d.seo_url || d.seoUrl || null,
        _raw: d,
      };
      openModal('projectDetails', projectData);
    } catch (err) {
      console.warn('Failed to load project details', err);
      openModal('projectDetails', notification.projectData || { id: null, title: 'Details unavailable', description: err?.message || String(err) });
    } finally {
      setPendingViewIds(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  return (
    <div className="relative">
        <button 
           onClick={() => navigate('more-settings/notifications')}
          className="relative rounded-full p-3 bg-light-blue-shade-20 hover:text-primary-blue transition-colors"
          aria-label="Notifications"
        >
          <img 
            src="/Icons/bell-icon.svg" 
            alt="notifications"
            className="w-4.5 h-4 object-cover"
          />
          {unreadCount > 0 && (
            <span className="absolute top-[2px] right-[6px] min-w-[16px] h-4 bg-green-500 rounded-full flex items-center justify-center text-white text-[10px] font-semibold px-1">
              {unreadCount}
            </span>
          )}
        </button>


      {/* Dropdown Panel */}
      {/* {open && (
        <div className="absolute right-0 mt-2 w-[560px] max-h-[420px] overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-xl z-50">
          {grouped.length === 0 ? (
            <div className="p-4 text-gray-500 text-center">No notifications</div>
          ) : (
            grouped.map((n) => {
              const isSuccess = typeof n.isSuccess !== 'undefined'
                ? Boolean(n.isSuccess)
                : (n.type === 'success');

              const description = n.description || n.message || (n.data && n.data.description) || '';
              const title = n.title || (typeof n.isSuccess !== 'undefined' ? (isSuccess ? 'Bid Success' : 'Bid Failed') : (n.type === 'success' ? 'Success' : (n.type === 'error' ? 'Error' : 'Info')));

              const accentColor = (typeof n.isSuccess !== 'undefined')
                ? (isSuccess ? '#28a745' : '#dc3545')
                : (n.type === 'success' ? '#28a745' : n.type === 'error' ? '#dc3545' : '#6c757d');

              const isErrorCard = (typeof n.isSuccess !== 'undefined' && n.isSuccess === false) || n.type === 'error';
              const cardBackground = n.read ? 'bg-white' : (isErrorCard ? 'bg-red-50' : 'bg-gray-50');
              const titleColor = isErrorCard ? 'text-red-700' : 'text-gray-900';
              const descriptionColor = isErrorCard ? 'text-red-600' : 'text-gray-700';
              
              return (
                <div
                  key={n.id}
                  className={`flex gap-6 p-3.5 border-b border-gray-100 ${cardBackground} ${isErrorCard ? 'border-l-4 border-l-red-500' : ''}`}
                >         
                  <div 
                    className="w-2 h-2 mt-1.5 rounded-full flex-shrink-0"
                    style={{ backgroundColor: accentColor }}
                  />
                  
                  <div className="flex-1">
                    <div className="flex justify-between items-start mb-1">
                      <span className={`font-semibold ${titleColor}`}>{title}</span>
                      <span className="text-xs text-gray-600">{formatTime(n.createdAt)}</span>
                    </div>
                    
                    <div className={`text-sm ${descriptionColor}`}>
                      {description || n.message || 'No additional details'}
                    </div>

                    <div className="mt-2 flex gap-2">
                      {n.projectData && (
                        <button 
                          onClick={() => handleView(n)} 
                          disabled={pendingViewIds.has(n.id)}
                          className="border border-gray-300 bg-white hover:bg-gray-50 text-green-600 disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-default px-2.5 py-1.5 rounded text-sm transition-colors"
                        >
                          {pendingViewIds.has(n.id) ? 'Loading…' : 'View'}
                        </button>
                      )}
                      
                      {!n.read && (
                        <button
                          onClick={() => handleMarkRead(n)}
                          disabled={pendingIds.has(n.id)}
                          className="border border-gray-300 bg-white hover:bg-gray-50 text-blue-600 disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-default px-2.5 py-1.5 rounded text-sm transition-colors"
                        >
                          {pendingIds.has(n.id) ? 'Saving…' : 'Mark as read'}
                        </button>
                      )}
                      
                      <button 
                        onClick={() => remove(n.id)} 
                        className="border border-gray-300 bg-white hover:bg-gray-50 text-red-600 px-2.5 py-1.5 rounded text-sm transition-colors"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )} */}

      {modalState.isOpen && modalState.type === 'projectDetails' && (
        <ProjectDetailsModal 
          projectData={modalState.data} 
          onClose={closeModal} 
        />
      )}
    </div>
  );
};

export default NotificationBell;
